<script lang="ts">
    import { BookClass, type Navigable } from "@circleofa/acim-engine"
    import { onDestroy } from "svelte"
    import Icon from "@components/material/Icon.svelte"
    import { navigatorOpen, openToNotation } from "@components/navigator/Navigator.svelte"
    import { cssObserver, resizeEntryBorderBoxBlockSize } from "@utils/directives/useCSSObserver"
    import { readingFrameHeaderHeight } from "@utils/material/responsive"

    export let page: Navigable

    type BreadcrumbSource = Navigable & {
        parent?: BreadcrumbSource | BookClass
        label?: string
        linkName?: [prefix: string, content: string]
    }

    type Breadcrumb = {
        label: string
        target?: string
    }

    const courseLabel = "A Course in Miracles CE"

    onDestroy(() => readingFrameHeaderHeight.set(0))

    let parentBreadcrumbs: Breadcrumb[] = []
    let currentLabel = ""

    $: (page, buildBreadcrumbs())

    function buildBreadcrumbs() {
        const pageTree: BreadcrumbSource[] = []
        let parent = page as BreadcrumbSource | BookClass | undefined

        while (parent && !(parent instanceof BookClass)) {
            pageTree.push(parent)
            parent = parent.parent
            if (pageTree.length > 6) break
        }

        pageTree.reverse()

        const current = pageTree[pageTree.length - 1]
        parentBreadcrumbs = pageTree.slice(0, -1).map((item) => ({
            label: formatBreadcrumbLabel(item),
            target: item.path,
        }))
        currentLabel = current ? formatBreadcrumbLabel(current) : page.label
    }

    function formatBreadcrumbLabel(item: BreadcrumbSource) {
        if (item.linkName) {
            return item.linkName.filter((part) => part.trim()).join(" ")
        }

        return item.label ?? item.path
    }

    function openCurrentPageInContents() {
        openToNotation(page.path, { exact: false })
    }

    function openBreadcrumbTarget(target: string) {
        openToNotation(target, { exact: true })
    }
</script>

<nav
    class="reading-frame-header"
    aria-label="Reading position"
    use:cssObserver={{
        store: readingFrameHeaderHeight,
        fn: resizeEntryBorderBoxBlockSize,
    }}
>
    <button
        type="button"
        class="toc-context"
        class:open={$navigatorOpen}
        aria-controls="navigator"
        aria-expanded={$navigatorOpen}
        aria-label="Open current page in table of contents"
        on:click={openCurrentPageInContents}
    >
        <span class="toc-icon" aria-hidden="true">
            <Icon icon="book" />
        </span>
        <span class="toc-label">Table of Contents</span>
    </button>

    <span class="breadcrumb-separator" aria-hidden="true">></span>
    <span class="breadcrumb-crumb course">
        <span class="breadcrumb-label">{courseLabel}</span>
    </span>

    {#each parentBreadcrumbs as breadcrumb}
        <span class="breadcrumb-separator" aria-hidden="true">-></span>
        {#if breadcrumb.target}
            <button
                type="button"
                class="breadcrumb-crumb optional breadcrumb-button"
                on:click={() => breadcrumb.target && openBreadcrumbTarget(breadcrumb.target)}
            >
                <span class="breadcrumb-label">{breadcrumb.label}</span>
            </button>
        {:else}
            <span class="breadcrumb-crumb optional">
                <span class="breadcrumb-label">{breadcrumb.label}</span>
            </span>
        {/if}
    {/each}

    <span class="breadcrumb-separator" aria-hidden="true">-></span>

    <span class="breadcrumb-crumb current">
        <span class="breadcrumb-label">{currentLabel}</span>
    </span>
</nav>

<style>
    .reading-frame-header {
        position: relative;
        z-index: 3;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 0.5rem;
        min-height: 3rem;
        width: 100%;
        margin: 0;
        padding: calc(var(--safe-area-inset-top, 0rem) + 0.5rem) 2rem 0.5rem;
        border: 0;
        border-bottom: 1px solid var(--color-purple-100);
        border-radius: 0;
        background: var(--color-grey-0);
        color: var(--color-grey-600);
        font-family:
            Inter,
            ui-sans-serif,
            system-ui,
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            sans-serif;
        text-align: left;
        text-transform: none;
        hyphens: none;
        overflow: hidden;
    }

    @media only screen and (min-width: 1154px) {
        .reading-frame-header {
            width: calc(100% - var(--reader-navigator-clearance, 0px));
            margin-left: var(--reader-navigator-clearance, 0px);
        }

        :global(html.expanded) .reading-frame-header {
            width: 100%;
            margin-left: 0;
        }
    }

    .toc-context {
        appearance: none;
        display: inline-grid;
        grid-auto-flow: column;
        align-items: center;
        gap: 0.4rem;
        flex: 0 0 auto;
        min-height: 1.75rem;
        min-width: 0;
        border: 1px solid var(--color-purple-100);
        border-radius: 0.5rem;
        background: var(--color-purple-50);
        color: var(--color-grey-600);
        cursor: pointer;
        font: inherit;
        font-size: 0.78rem;
        font-weight: 750;
        line-height: 1;
        letter-spacing: 0;
        padding: 0.25rem 0.5rem;
        text-align: left;
        text-transform: none;
        transition:
            background-color 0.16s ease,
            border-color 0.16s ease,
            color 0.16s ease,
            transform 0.16s ease;
        white-space: nowrap;
    }

    .toc-context:hover,
    .toc-context.open {
        border-color: var(--color-purple-100);
        background: var(--color-purple-100);
        color: var(--color-purple-700);
    }

    .toc-context:active {
        transform: translateY(1px);
    }

    .toc-context:focus-visible {
        outline: 2px solid var(--color-gold-400);
        outline-offset: 2px;
    }

    .toc-icon {
        display: grid;
        place-items: center;
        width: 1rem;
        height: 1rem;
        flex: 0 0 auto;
    }

    .toc-icon :global(svg) {
        width: 1rem;
        height: 1rem;
    }

    .reading-frame-header span {
        line-height: 1;
    }

    .breadcrumb-crumb,
    .breadcrumb-separator {
        color: var(--color-grey-500);
        font-size: 0.76rem;
        font-weight: 650;
        line-height: 1;
        white-space: nowrap;
    }

    .breadcrumb-crumb {
        display: inline-flex;
        align-items: center;
        flex: 0 1 auto;
        min-width: 0;
        max-width: 12rem;
    }

    .breadcrumb-button {
        appearance: none;
        border: 0;
        background: transparent;
        cursor: pointer;
        font-family: inherit;
        line-height: 1;
        padding: 0;
        text-align: left;
    }

    .breadcrumb-button .breadcrumb-label {
        line-height: 1.2;
    }

    .breadcrumb-button:hover .breadcrumb-label {
        text-decoration: underline;
        text-underline-offset: 0.12em;
    }

    .breadcrumb-button:focus-visible {
        outline: 2px solid var(--color-gold-400);
        outline-offset: 2px;
        border-radius: 0.2rem;
    }

    .breadcrumb-crumb.optional {
        flex: 0 1 auto;
        max-width: 10rem;
    }

    .breadcrumb-crumb.current {
        max-width: min(20rem, 100%);
        color: var(--color-grey-700);
        font-weight: 750;
    }

    .breadcrumb-separator {
        flex: 0 0 auto;
        color: var(--color-grey-300);
    }

    .breadcrumb-label,
    .toc-label {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    :global(.darkmode) .reading-frame-header {
        border-color: var(--color-grey-800);
        background: var(--color-grey-900);
        color: var(--color-grey-200);
    }

    :global(.darkmode) .breadcrumb-crumb {
        color: var(--color-grey-300);
    }

    :global(.darkmode) .breadcrumb-crumb.current {
        color: var(--color-grey-100);
    }

    :global(.darkmode) .breadcrumb-separator {
        color: var(--color-grey-500);
    }

    :global(.darkmode) .toc-context {
        border-color: var(--color-grey-700);
        background: var(--color-grey-800);
        color: var(--color-grey-200);
    }

    :global(.darkmode) .toc-context:hover,
    :global(.darkmode) .toc-context.open {
        border-color: var(--color-grey-600);
        background: var(--color-grey-700);
        color: var(--color-grey-0);
    }

    :global(.reader-layout-compact) .reading-frame-header {
        display: none;
    }
</style>
